import { db } from "../lib/firebase";
import {
  collection,
  doc,
  increment,
  runTransaction,
  serverTimestamp,
} from "../lib/firestoreClient";

export const IMPACT_LEADERBOARD_CATEGORIES = {
  TOP_GROWERS: "topGrowers",
  PRICE_SCOUTS: "priceScouts",
  FIXERS: "fixers",
};

export const IMPACT_ACTIONS = {
  PRODUCT_POSTED: "product_posted",
  PRICE_REPORT_VALIDATED: "price_report_validated",
  PRODUCT_RATING_RECEIVED: "product_rating_received",
  HELPFUL_FEEDBACK: "helpful_feedback",
};

const ACTION_CONFIG = {
  [IMPACT_ACTIONS.PRODUCT_POSTED]: {
    points: 5,
    category: IMPACT_LEADERBOARD_CATEGORIES.TOP_GROWERS,
    breakdownField: "productListingPoints",
  },
  [IMPACT_ACTIONS.PRICE_REPORT_VALIDATED]: {
    points: 10,
    category: IMPACT_LEADERBOARD_CATEGORIES.PRICE_SCOUTS,
    breakdownField: "priceValidatedPoints",
  },
  [IMPACT_ACTIONS.PRODUCT_RATING_RECEIVED]: {
    points: 1,
    category: IMPACT_LEADERBOARD_CATEGORIES.TOP_GROWERS,
    breakdownField: "productRatingPoints",
  },
  [IMPACT_ACTIONS.HELPFUL_FEEDBACK]: {
    points: 10,
    category: IMPACT_LEADERBOARD_CATEGORIES.FIXERS,
    breakdownField: "helpfulFeedbackPoints",
  },
};

function resolveActionConfig(action, explicitPoints) {
  const config = ACTION_CONFIG[action];
  if (!config) {
    throw new Error(`Unknown impact action: ${action}`);
  }

  const resolvedPoints = Number.isFinite(Number(explicitPoints))
    ? Number(explicitPoints)
    : config.points;

  return {
    ...config,
    points: resolvedPoints,
  };
}

function buildUserImpactIncrement(config) {
  const categoryPath = `impactBreakdown.categories.${config.category}`;
  const breakdownPath = `impactBreakdown.${config.breakdownField}`;

  const patch = {
    points: increment(config.points),
    updatedAt: serverTimestamp(),
    [breakdownPath]: increment(config.points),
    [categoryPath]: increment(config.points),
  };

  if (config.breakdownField.includes("Rating")) {
    patch.ratingPoints = increment(config.points);
  }

  return patch;
}

function createEventPayload({ userId, action, config, metadata }) {
  return {
    userId,
    action,
    category: config.category,
    points: config.points,
    metadata: metadata || {},
    createdAt: serverTimestamp(),
  };
}

export function awardImpactPointsInTransaction(tx, { userId, action, points, metadata = {} }) {
  if (!userId) throw new Error("userId is required");

  const config = resolveActionConfig(action, points);
  if (config.points <= 0) return;

  const userRef = doc(db, "users", String(userId));
  const eventRef = doc(collection(db, "impact_points_events"));

  tx.set(userRef, buildUserImpactIncrement(config), { merge: true });
  tx.set(eventRef, createEventPayload({ userId, action, config, metadata }));
}

export async function awardImpactPoints({ userId, action, points, metadata = {} }) {
  await runTransaction(db, async (tx) => {
    awardImpactPointsInTransaction(tx, { userId, action, points, metadata });
  });
}

export function getImpactActionConfig(action) {
  return ACTION_CONFIG[action] || null;
}
